import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import TopBar from '../components/TopBar';
import MobileNavTabs from '../components/MobileNavTabs';
import DesktopSidebar from '../components/DesktopSidebar';
import ResolvedStorageImage from '../components/ResolvedStorageImage';
import layoutStyles from '../styles/layout.module.css';
import desktopLayout from '../components/DesktopSidebarLayout.module.css';
import styles from './Creator.module.css';
import { UserContext } from '../App';
import { db } from '../firebase';
import { deriveActiveConnectStatus } from '../utils/stripeConnect';
import { getRoleAccessState, ROLE_ARTIST } from '../utils/roles';
import { getArtistPhotoPath } from '../utils/storageMedia';

export default function Creator() {
  const appUser = useContext(UserContext);
  const navigate = useNavigate();
  const [artistProfile, setArtistProfile] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const artistAccess = getRoleAccessState(appUser, ROLE_ARTIST);
  const connectStatus = deriveActiveConnectStatus(appUser);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const [artistSnap, dashboardSnap] = await Promise.all([
          appUser?.artistId ? getDoc(doc(db, 'artists', appUser.artistId)) : Promise.resolve(null),
          appUser?.id ? getDoc(doc(db, 'artist_dashboards', appUser.id)) : Promise.resolve(null),
        ]);
        if (!active) return;
        setArtistProfile(artistSnap && artistSnap.exists() ? { id: artistSnap.id, ...(artistSnap.data() || {}) } : null);
        setDashboard(dashboardSnap && dashboardSnap.exists() ? dashboardSnap.data() || {} : null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [appUser?.artistId, appUser?.id]);

  const summary = dashboard?.summary || {};

  return (
    <div className={layoutStyles.homeContainer} style={{ paddingBottom: 0 }}>
      <TopBar hideLeft>
        <div className={desktopLayout.mobileTabs}>
          <MobileNavTabs />
        </div>
      </TopBar>

      <div className={`${desktopLayout.shell} ${desktopLayout.shellWithRail}`.trim()}>
        <DesktopSidebar active="artist" note="Creator workspace for artist setup, products, and payment readiness." />
        <div className={desktopLayout.main}>
          <div className={desktopLayout.mainInner}>
            <div className={styles.pageShell}>
              <header className={styles.header}>
                <h1>Creator</h1>
                <p>Manage your artist profile, product catalog, and payout readiness from a single clean workspace.</p>
              </header>

              <section className={styles.card}>
                <div className={styles.sectionHeader}>
                  <h3>Artist Profile</h3>
                  <p>{artistProfile ? 'Your public artist presence is live and can be edited anytime.' : 'Create your artist profile before launching products.'}</p>
                </div>
                {loading ? <p className={styles.statusNote}>Loading creator data…</p> : null}
                {artistProfile ? (
                  <div className={styles.artistRow}>
                    <div className={styles.artistAvatar}>
                      <ResolvedStorageImage path={getArtistPhotoPath(artistProfile, 'thumb')} alt={artistProfile.artistFullName || 'Artist'} />
                    </div>
                    <div>
                      <p className={styles.artistName}>{artistProfile.artistFullName || 'Artist'}</p>
                      <p className={styles.statusNote}>Artist ID: {artistProfile.artistId || artistProfile.id}</p>
                    </div>
                  </div>
                ) : <p className={styles.statusNote}>No artist profile yet.</p>}
                <div className={styles.actionGrid}>
                  <button type="button" onClick={() => navigate('/creator/profile')}>{artistProfile ? 'Edit Artist Profile' : 'Create Artist Profile'}</button>
                  {artistProfile ? <button type="button" onClick={() => navigate(`/artists/${artistProfile.artistId || artistProfile.id}`)}>Open Public Artist Page</button> : null}
                </div>
              </section>

              <section className={styles.card}>
                <div className={styles.sectionHeader}>
                  <h3>Catalog Summary</h3>
                  <p>Catalog totals stay count-based across all products, while new launches now happen through single-drop publishing.</p>
                </div>
                <div className={styles.statusGrid}>
                  <div className={styles.statusItem}><span>Collectibles</span><strong>{summary.productCount || 0}</strong></div>
                  <div className={styles.statusItem}><span>Total Sold</span><strong>{summary.totalSold || 0}</strong></div>
                </div>
                <div className={styles.actionGrid}>
                  <button type="button" onClick={() => navigate('/creator/products')}>Open Drops</button>
                </div>
              </section>

              <section className={styles.card}>
                <div className={styles.sectionHeader}>
                  <h3>Payment Dashboard</h3>
                  <p>You can publish and sell with an active artist subscription and profile. Stripe payout setup can finish later while your creator share stays held until release.</p>
                </div>
                <div className={styles.statusGrid}>
                  <div className={styles.statusItem}><span>Artist Access</span><strong>{artistAccess.isActive ? 'Active' : 'Inactive'}</strong></div>
                  <div className={styles.statusItem}><span>Stripe Express</span><strong>{connectStatus.canCharge ? (connectStatus.canPayout ? 'Fully Enabled' : 'Can Charge') : (connectStatus.accountId ? 'Setup Required' : 'Not Connected')}</strong></div>
                </div>
                <div className={styles.actionGrid}>
                  <button type="button" onClick={() => navigate('/payment')}>{connectStatus.accountId ? 'Open Payment' : 'Set Up Payouts'}</button>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
